import dayjs from 'dayjs';
import PaiementsApi from '../../../../../api/PaiementsApi';
import UserApi from '../../../../../api/UserApi';
import BalanceValidationApi from '../../../../../api/BalanceValidationApi';
import { normalizeUserPaymentMethodForPicker } from './transferUserPaymentMethods';

const normalizeLabel = (raw) =>
  String(raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/** Physique transfert : espèces / cash uniquement (aligné moteur réservation). */
export function isCashLikePaymentMode(mode) {
  const lab = normalizeLabel(mode?.label || mode?.libelle || mode?.code || mode?.nom || '');
  return lab.includes('espece') || lab.includes('cash');
}

const isAdminManualAllowedPaymentLabel = (raw) => {
  const l = normalizeLabel(raw);
  if (l.includes('espece') || l.includes('cash')) return true;
  if (l.includes('check') || l.includes('cheque')) return true;
  if ((l.includes('carte') && l.includes('bancaire')) || l.includes('carte bancaire')) return true;
  return false;
};

const toNumSet = (arr) => {
  const s = new Set();
  (Array.isArray(arr) ? arr : []).forEach((x) => {
    const n =
      typeof x === 'object' && x != null ? Number(x.id ?? x.paiement_method_id ?? x.mode_id) : Number(x);
    if (!Number.isNaN(n)) s.add(n);
  });
  return s;
};

/** Backend refuse totalAmount = 0 (`!amountNum`). */
function eligibilityAmount(totalAmount) {
  const n = Number(totalAmount);
  if (Number.isFinite(n) && n > 0) return n;
  return 0.01;
}

function checkInFromPickup(pickup) {
  if (!pickup) return null;
  const d = dayjs(pickup);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
}

/**
 * Modes affichés dans le picker transfert (admin + facturation moteur hôtel).
 * - physique : modes espèces/cash autorisés sur le profil connecté.
 * - morale + partenaire : POST partner/:id/payment-eligibility puis intersection profil.
 * - morale sans clé partenaire : modes du profil (`getPaymentMethodsByUserId`).
 */
export async function loadTransferPaymentMethodsForPicker({
  userId,
  billingClientKind,
  billingClientId,
  billingPartnerId,
  checkInDateRaw,
  totalAmount,
}) {
  const userData = await UserApi.getById(userId);
  const userPm = userData?.user?.paiement_methods ?? userData?.paiement_methods ?? [];
  const userIdSet = toNumSet(userPm);

  const allModes = await PaiementsApi.getAllModesWithChamps();
  const all = Array.isArray(allModes) ? allModes : [];
  const allById = new Map(all.map((m) => [Number(m.id), m]));

  const filterUser = (rows) =>
    rows
      .filter((m) => m?.id != null && userIdSet.has(Number(m.id)))
      .map((m) => {
        const base = allById.get(Number(m.id));
        const merged = base ? { ...base, ...m } : m;
        return normalizeUserPaymentMethodForPicker(merged);
      })
      .filter(Boolean);

  const listFromUserApi = async () => {
    const res = await UserApi.getPaymentMethodsByUserId(userId);
    return (res?.paymentMethods ?? [])
      .map((m) => normalizeUserPaymentMethodForPicker(m))
      .filter(Boolean);
  };

  if (billingClientKind === 'physique') {
    const cash = all.filter((m) => m?.id != null && userIdSet.has(Number(m.id)) && isCashLikePaymentMode(m));
    return cash.map((m) => normalizeUserPaymentMethodForPicker(m)).filter(Boolean);
  }

  const parsedPartner =
    billingPartnerId != null && billingPartnerId !== '' ? Number(billingPartnerId) : NaN;
  const parsedClient =
    billingClientId != null && billingClientId !== '' ? Number(billingClientId) : NaN;
  const partnerIdForApi =
    billingClientKind === 'morale' && Number.isFinite(parsedPartner) && parsedPartner > 0
      ? parsedPartner
      : billingClientKind === 'morale' && Number.isFinite(parsedClient) && parsedClient > 0
        ? parsedClient
        : null;

  if (billingClientKind === 'morale' && partnerIdForApi) {
    const adminManualFallback = () =>
      all
        .filter((m) => {
          const lab = m.label || m.libelle || m.code || '';
          return m?.id != null && userIdSet.has(Number(m.id)) && isAdminManualAllowedPaymentLabel(lab);
        })
        .map((m) => normalizeUserPaymentMethodForPicker(m))
        .filter(Boolean);

    try {
      const raw = await BalanceValidationApi.getPartnerPaymentEligibility(partnerIdForApi, {
        checkInDate: checkInFromPickup(checkInDateRaw),
        totalAmount: eligibilityAmount(totalAmount),
      });
      const eligibility = raw?.data ?? {};
      let rows = Array.isArray(eligibility.partnerPaymentMethods) ? eligibility.partnerPaymentMethods : [];
      const ids = Array.isArray(eligibility.partnerPaymentMethodIds)
        ? eligibility.partnerPaymentMethodIds
        : [];
      if (!rows.length && ids.length) {
        rows = ids.map((id) => all.find((m) => Number(m.id) === Number(id))).filter(Boolean);
      }
      let list = filterUser(rows);
      if (!list.length && ids.length) {
        list = filterUser(ids.map((id) => allById.get(Number(id))).filter(Boolean));
      }
      if (!list.length) {
        return adminManualFallback();
      }
      return list;
    } catch {
      return adminManualFallback();
    }
  }

  if (billingClientKind === 'morale') {
    return listFromUserApi();
  }

  return listFromUserApi();
}
